import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import '@fontsource-variable/inter';
import '@fontsource/ibm-plex-sans-arabic/400.css';
import '@fontsource/ibm-plex-sans-arabic/500.css';
import '@fontsource/ibm-plex-sans-arabic/600.css';
import { createAppTheme, type ColorMode, type Direction } from './createAppTheme';

const MODE_STORAGE_KEY = 'radiology.colorMode';
const DIRECTION_STORAGE_KEY = 'radiology.direction';

interface AppThemeContextValue {
  mode: ColorMode;
  setMode: (mode: ColorMode) => void;
  toggleMode: () => void;
  direction: Direction;
  setDirection: (direction: Direction) => void;
}

const AppThemeContext = createContext<AppThemeContextValue | null>(null);

export function useAppTheme(): AppThemeContextValue {
  const ctx = useContext(AppThemeContext);
  if (!ctx) throw new Error('useAppTheme must be used inside <AppThemeProvider>');
  return ctx;
}

// Two caches, created once. Swapping between them is what actually flips the
// generated CSS (padding-left <-> padding-right etc.); setting theme.direction
// alone only flips MUI's own logic and leaves raw CSS untouched.
const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] });
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  const stored = window.localStorage.getItem(key);
  return allowed.includes(stored as T) ? (stored as T) : fallback;
}

export interface AppThemeProviderProps {
  children: ReactNode;
  /** Overrides the stored/system preference. Mainly for tests and Storybook. */
  defaultMode?: ColorMode;
  defaultDirection?: Direction;
}

/**
 * Provides the theme, colour mode and text direction.
 *
 * Direction is wired up now even though the UI ships English-only (decision #3),
 * so switching on Arabic later is a translation task rather than a refactor.
 */
export function AppThemeProvider({
  children,
  defaultMode,
  defaultDirection,
}: AppThemeProviderProps) {
  const [mode, setModeState] = useState<ColorMode>(
    () => defaultMode ?? readStored(MODE_STORAGE_KEY, ['light', 'dark'] as const, 'light'),
  );
  const [direction, setDirectionState] = useState<Direction>(
    () => defaultDirection ?? readStored(DIRECTION_STORAGE_KEY, ['ltr', 'rtl'] as const, 'ltr'),
  );

  // Respect the OS preference on first visit, but never override an explicit choice.
  useEffect(() => {
    if (defaultMode || window.localStorage.getItem(MODE_STORAGE_KEY)) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    setModeState(query.matches ? 'dark' : 'light');
    const onChange = (e: MediaQueryListEvent) => setModeState(e.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [defaultMode]);

  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', direction === 'rtl' ? 'ar' : 'en');
    document.documentElement.style.colorScheme = mode;
  }, [direction, mode]);

  const setMode = useCallback((next: ColorMode) => {
    setModeState(next);
    window.localStorage.setItem(MODE_STORAGE_KEY, next);
  }, []);

  const setDirection = useCallback((next: Direction) => {
    setDirectionState(next);
    window.localStorage.setItem(DIRECTION_STORAGE_KEY, next);
  }, []);

  const toggleMode = useCallback(
    () => setMode(mode === 'light' ? 'dark' : 'light'),
    [mode, setMode],
  );

  const theme = useMemo(() => createAppTheme(mode, direction), [mode, direction]);
  const value = useMemo(
    () => ({ mode, setMode, toggleMode, direction, setDirection }),
    [mode, setMode, toggleMode, direction, setDirection],
  );

  return (
    <AppThemeContext.Provider value={value}>
      <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
        <MuiThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </CacheProvider>
    </AppThemeContext.Provider>
  );
}
